import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { UploadSimple, Warning } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const ImportDialog = ({ open, onOpenChange, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [autoCreateClients, setAutoCreateClients] = useState(true);
  const [previewData, setPreviewData] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      if (validTypes.includes(fileExtension)) {
        setFile(selectedFile);
        setPreviewData(null);
      } else {
        toast.error('Please select a CSV or Excel file');
      }
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/sales/import/preview`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (data.duplicate_count > 0) {
        setPreviewData(data);
      } else {
        await doImport('skip');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const doImport = async (duplicateAction) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/sales/import?auto_create_clients=${autoCreateClients}&duplicate_action=${duplicateAction}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const parts = [`${data.imported} sales imported`];
      if (data.created_clients > 0) parts.push(`${data.created_clients} clients created`);
      if (data.duplicates > 0) parts.push(`${data.duplicates} duplicates skipped`);
      if (data.replaced > 0) parts.push(`${data.replaced} replaced`);
      if (data.skipped > 0) parts.push(`${data.skipped} skipped`);
      if (data.column_mapping) {
        const mapped = Object.entries(data.column_mapping).map(([k, v]) => `"${v}" → ${k}`).join(', ');
        parts.push(`Columns: ${mapped}`);
      }
      toast.success(`Import complete: ${parts.join(', ')}`, { duration: 6000 });
      resetState();
      onOpenChange(false);
      if (onImportSuccess) onImportSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const resetState = () => {
    setFile(null);
    setPreviewData(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetState(); onOpenChange(v); }}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight">
            {previewData ? 'Duplicates Detected' : 'Import Sales Data'}
          </DialogTitle>
        </DialogHeader>

        {!previewData ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
                Upload File (CSV or Excel)
              </Label>
              <p className="text-sm text-muted-foreground">
                Headers are auto-detected. Your columns can be named flexibly:<br />
                e.g., "Customer Name", "Party", "Buyer" → client name<br />
                e.g., "Sale Amount", "Total", "Value" → amount<br />
                e.g., "Invoice No", "Bill Number", "Ref No" → invoice number
              </p>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="w-full p-2 border border-border rounded-sm"
                data-testid="file-upload-input"
              />
              {file && (
                <div className="text-sm text-accent">
                  Selected: {file.name}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
              <div>
                <Label className="text-sm font-medium">Auto-create missing clients</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Automatically create clients that don't exist yet
                </p>
              </div>
              <Switch
                checked={autoCreateClients}
                onCheckedChange={setAutoCreateClients}
                data-testid="auto-create-toggle"
              />
            </div>

            <Button
              onClick={handlePreview}
              disabled={!file || uploading || previewing}
              className="w-full rounded-sm"
              data-testid="upload-button"
            >
              {previewing ? 'Checking for duplicates...' : uploading ? 'Uploading...' : 'Import Data'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="duplicate-detection-panel">
            <div className="flex items-start gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-sm">
              <Warning size={20} weight="fill" className="text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-yellow-700 dark:text-yellow-300">
                  {previewData.duplicate_count} duplicate{previewData.duplicate_count > 1 ? 's' : ''} found
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Matched by invoice number + date + amount in "{previewData.filename}" ({previewData.total_rows} rows)
                </div>
              </div>
            </div>

            <div className="max-h-52 overflow-y-auto border border-border rounded-sm">
              <table className="w-full text-sm" data-testid="duplicates-table">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b border-border">
                    <th className="text-left py-1.5 px-2 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Row</th>
                    <th className="text-left py-1.5 px-2 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Invoice</th>
                    <th className="text-right py-1.5 px-2 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Amount</th>
                    <th className="text-left py-1.5 px-2 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Date</th>
                    <th className="text-left py-1.5 px-2 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Client</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.duplicates.map((d, i) => (
                    <tr key={i} className="border-b border-border" data-testid={`dup-row-${i}`}>
                      <td className="py-1.5 px-2 text-muted-foreground">{d.row}</td>
                      <td className="py-1.5 px-2 font-medium">{d.invoice_number}</td>
                      <td className="py-1.5 px-2 text-right">₹{d.amount.toLocaleString()}</td>
                      <td className="py-1.5 px-2">{d.date}</td>
                      <td className="py-1.5 px-2 text-muted-foreground">{d.client_name || d.existing_client}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-2">
              <Button
                onClick={() => doImport('skip')}
                disabled={uploading}
                variant="outline"
                className="w-full rounded-sm"
                data-testid="skip-duplicates-btn"
              >
                {uploading ? 'Importing...' : `Skip Duplicates (import ${previewData.total_rows - previewData.duplicate_count} new)`}
              </Button>
              <Button
                onClick={() => doImport('replace')}
                disabled={uploading}
                className="w-full rounded-sm"
                data-testid="replace-duplicates-btn"
              >
                {uploading ? 'Importing...' : `Replace Duplicates (overwrite ${previewData.duplicate_count} existing)`}
              </Button>
              <Button
                onClick={() => setPreviewData(null)}
                variant="ghost"
                className="w-full rounded-sm text-muted-foreground"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
